/**
 * WebSocket service for real-time sync notifications
 */

export interface SyncMessage {
  type: 'sync_completed' | 'sync_error' | 'forecast_updated' | 'battery_schedules_updated' | 'battery_execution_completed' | 'realtime_data_update' | 'manual_override_updated' | 'ping' | 'pong';
  timestamp: string;
  message?: string;
  status?: 'success' | 'error' | 'running';
  data?: any;
  user_id?: number;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private heartbeatIntervalMs = 25000;
  private isConnecting = false;
  private shouldReconnect = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private messageHandlers: ((message: SyncMessage) => void)[] = [];
  private connectionStateHandlers: ((connected: boolean) => void)[] = [];

  constructor(private baseUrl: string = '') {
    this.baseUrl = baseUrl || process.env.REACT_APP_API_URL || 'http://localhost:8000';
    // Convert http(s):// to ws(s)://
    this.baseUrl = this.baseUrl.replace(/^http/, 'ws');

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    this.shouldReconnect = true;

    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.clearReconnectTimer();
    this.detachSocket();

    this.isConnecting = true;
    const accessToken = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
    const qs = accessToken ? `?token=${encodeURIComponent(accessToken)}` : '';
    const wsUrl = `${this.baseUrl}/ws/sync${qs}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('🔌 WebSocket connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.notifyConnectionState(true);
      };

      this.ws.onmessage = (event) => {
        try {
          const message: SyncMessage = JSON.parse(event.data);
          if (message.type === 'ping' || message.type === 'pong') {
            return;
          }
          console.log('📨 WebSocket message received:', message);
          this.notifyMessageHandlers(message);
        } catch (error) {
          console.error('❌ Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.stopHeartbeat();
        this.ws = null;
        this.notifyConnectionState(false);

        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        // onclose follows and handles reconnect
      };

    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      this.notifyConnectionState(false);
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Disconnect from the WebSocket server and stop reconnecting
   */
  disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    this.stopHeartbeat();
    this.detachSocket(true);
    this.isConnecting = false;
    this.notifyConnectionState(false);
  }

  /**
   * Add a message handler
   */
  onMessage(handler: (message: SyncMessage) => void): () => void {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Add a connection state handler
   */
  onConnectionStateChange(handler: (connected: boolean) => void): () => void {
    this.connectionStateHandlers.push(handler);
    return () => {
      const index = this.connectionStateHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionStateHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private handleOnline = (): void => {
    this.reconnectNow('network online');
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.reconnectNow('tab visible');
    }
  };

  private reconnectNow(reason: string): void {
    if (!this.shouldReconnect || this.isConnected() || this.isConnecting) {
      return;
    }
    console.log(`🔄 WebSocket reconnecting immediately (${reason})`);
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    this.connect();
  }

  private detachSocket(close: boolean = false): void {
    if (!this.ws) {
      return;
    }
    this.ws.onopen = null;
    this.ws.onmessage = null;
    this.ws.onerror = null;
    this.ws.onclose = null;
    if (close && this.ws.readyState !== WebSocket.CLOSED) {
      this.ws.close(1000, 'Client disconnect');
    }
    this.ws = null;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (!this.shouldReconnect || this.reconnectTimer || this.isConnecting) {
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`🔄 Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }

  private notifyMessageHandlers(message: SyncMessage): void {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('❌ Error in message handler:', error);
      }
    });
  }

  private notifyConnectionState(connected: boolean): void {
    this.connectionStateHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        console.error('❌ Error in connection state handler:', error);
      }
    });
  }
}

// Create a singleton instance
export const websocketService = new WebSocketService();
